/**
 * S13 — Drawer de detalhe de fato do People Memory.
 * BLOQUEIO CRÍTICO: o select NÃO oferece `confirmed_by_subject`. Trigger SQL
 * `block_admin_subject_confirm` rejeita mesmo via API direta.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, X, Pencil, Send } from "lucide-react";

export function PeopleMemoryDrawer({ factId, onClose }: { factId: string | null; onClose: () => void }) {
  const open = !!factId;
  const qc = useQueryClient();
  const { user } = useAuth();
  const [justification, setJustification] = useState("");
  const [newValue, setNewValue] = useState("");
  const [editing, setEditing] = useState(false);

  const { data: fact } = useQuery<any>({
    enabled: open,
    queryKey: ["pm-fact", factId],
    queryFn: async () => {
      const { data } = await supabase.from("syra_person_memories").select("*").eq("id", factId!).maybeSingle();
      return data;
    },
  });

  const { data: audit = [] } = useQuery<any[]>({
    enabled: open,
    queryKey: ["pm-audit", factId],
    queryFn: async () => {
      const { data } = await supabase
        .from("syra_person_memory_audit")
        .select("*")
        .eq("memory_id", factId!)
        .order("created_at", { ascending: false })
        .limit(50);
      return (data ?? []) as any[];
    },
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["pm-fact", factId] });
    qc.invalidateQueries({ queryKey: ["pm-audit", factId] });
    qc.invalidateQueries({ queryKey: ["pm-facts"] });
  };

  const confirmAsAdmin = async () => {
    if (justification.trim().length < 20) {
      toast.error("Justificativa precisa de pelo menos 20 caracteres.");
      return;
    }
    const updatePayload: any = { confirmation_status: "confirmed_by_admin" };
    const { error } = await supabase.from("syra_person_memories").update(updatePayload).eq("id", factId!);
    if (error) { toast.error(error.message); return; }
    const auditPayload: any = {
      org_id: fact!.org_id,
      memory_id: factId!,
      event_type: "confirmed_by_admin",
      actor_kind: "admin",
      actor_id: user?.id ?? null,
      payload: { justification },
    };
    await supabase.from("syra_person_memory_audit").insert(auditPayload);
    toast.success("Fato confirmado pelo admin.");
    setJustification("");
    refresh();
  };

  const reject = async () => {
    const updatePayload: any = { confirmation_status: "archived" };
    const { error } = await supabase.from("syra_person_memories").update(updatePayload).eq("id", factId!);
    if (error) { toast.error(error.message); return; }
    const auditPayload: any = {
      org_id: fact!.org_id, memory_id: factId!, event_type: "archived",
      actor_kind: "admin", actor_id: user?.id ?? null, payload: {},
    };
    await supabase.from("syra_person_memory_audit").insert(auditPayload);
    toast.success("Fato arquivado.");
    refresh();
  };

  const saveEdit = async () => {
    if (!newValue.trim()) { toast.error("Valor vazio."); return; }
    const updatePayload: any = { value: newValue };
    const { error } = await supabase.from("syra_person_memories").update(updatePayload).eq("id", factId!);
    if (error) { toast.error(error.message); return; }
    const auditPayload: any = {
      org_id: fact!.org_id, memory_id: factId!, event_type: "value_edited",
      actor_kind: "admin", actor_id: user?.id ?? null,
      payload: { old: fact!.value, new: newValue },
    };
    await supabase.from("syra_person_memory_audit").insert(auditPayload);
    toast.success("Valor atualizado.");
    setEditing(false);
    refresh();
  };

  const forceDM = async () => {
    const dmPayload: any = {
      group_id: fact!.group_id,
      memory_id: factId!,
      subject_jid: fact!.subject_jid,
      instance_name: "admin_override",
      template_used: "admin_force",
      status: "queued",
    };
    const { error } = await supabase.from("syra_person_memory_dm_log").insert(dmPayload);
    if (error) { toast.error(error.message); return; }
    const auditPayload: any = {
      org_id: fact!.org_id, memory_id: factId!, event_type: "dm_force_queued",
      actor_kind: "admin", actor_id: user?.id ?? null, payload: { reason: "admin_force_dm" },
    };
    await supabase.from("syra_person_memory_audit").insert(auditPayload);
    toast.success("DM enfileirado (override de rate-limit).");
    refresh();
  };

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="max-h-[90vh]">
        <DrawerHeader>
          <DrawerTitle>
            {fact?.subject_display_name || fact?.subject_jid} · <code className="text-sm">{fact?.attribute}</code>
          </DrawerTitle>
          <DrawerDescription>
            <span className="font-mono">{fact?.value}</span>
            <span className="ml-2"><Badge>{fact?.confirmation_status}</Badge></span>
            {fact?.is_sensitive && <Badge variant="destructive" className="ml-2">sensível</Badge>}
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 space-y-4 overflow-y-auto">
          <div className="space-y-3 border rounded-md p-3">
            <h3 className="text-sm font-semibold">Ações</h3>

            <div>
              <Label className="text-xs">Justificativa (mín. 20 caracteres)</Label>
              <Textarea value={justification} onChange={(e) => setJustification(e.target.value)} rows={2} />
              <Button size="sm" className="mt-2" onClick={confirmAsAdmin}>
                <CheckCircle2 className="w-4 h-4 mr-2" />Confirmar como admin
              </Button>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="destructive" onClick={reject}>
                <X className="w-4 h-4 mr-2" />Arquivar
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setEditing((e) => !e); setNewValue(fact?.value ?? ""); }}>
                <Pencil className="w-4 h-4 mr-2" />Editar valor
              </Button>
              <Button size="sm" variant="outline" onClick={forceDM}>
                <Send className="w-4 h-4 mr-2" />Forçar DM agora
              </Button>
            </div>

            {editing && (
              <div className="space-y-2">
                <Input value={newValue} onChange={(e) => setNewValue(e.target.value)} />
                <Button size="sm" onClick={saveEdit}>Salvar</Button>
              </div>
            )}

            <p className="text-xs text-muted-foreground border-t pt-2">
              ⚠️ Admin <b>não pode</b> marcar <code>confirmed_by_subject</code> — apenas o próprio sujeito,
              via DM/reaction/auto-declaração. Tentativas são bloqueadas pelo banco.
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-2">Histórico</h3>
            <div className="space-y-2">
              {audit.map((a: any) => (
                <div key={a.id} className="text-xs border-l-2 border-muted pl-3 py-1">
                  <div className="font-medium">{a.event_type}</div>
                  <div className="text-muted-foreground">
                    {new Date(a.created_at).toLocaleString()} · {a.actor_kind}
                  </div>
                  {a.payload && Object.keys(a.payload).length > 0 && (
                    <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-x-auto">
                      {JSON.stringify(a.payload, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
              {audit.length === 0 && <div className="text-xs text-muted-foreground">Sem histórico.</div>}
            </div>
          </div>
        </div>

        <DrawerFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
